// TimeSheetEntry.tsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  setDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { format, startOfWeek, addDays, subWeeks, isAfter } from "date-fns";
import { db } from "../../firebase/firebase";
import { useColorMode } from "@chakra-ui/react";
import { getThemeClasses } from "../../utils/themeUtils";
import { formatDateDDMMYYYY } from "../../utils/dateUtils";
import CustomDropdown from "../CustomDropdown";
import { useAuth } from "../../hooks/useAuth";
import { getTimesheetPermissions } from "../../utils/permissions";
import { showToast } from "../../utils/toastUtils";
import { fetchLeaveEntries } from "../../utils/leaveUtils";

// Inline edit icon SVG component to avoid react-icons type issues
const EditSvg: React.FC<{ size?: number; className?: string }> = ({
  size = 16,
  className = "",
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </svg>
);

// Red trash (delete) icon SVG
const TrashSvg: React.FC<{ size?: number; className?: string }> = ({
  size = 16,
  className = "",
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const AutoResizingTextarea: React.FC<any> = ({ value, onChange, placeholder, className, isDark, onInput, ...props }) => {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const adjustHeight = () => {
    const target = textareaRef.current;
    if (target) {
      target.style.height = 'auto';
      target.style.height = target.scrollHeight + 'px';
    }
  };

  React.useLayoutEffect(() => {
    adjustHeight();
  }, [value]);

  return (
    <textarea
      ref={textareaRef}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={className}
      onInput={(e) => {
        adjustHeight();
        if (onInput) onInput(e);
      }}
      {...props}
    />
  );
};

interface TimeSheetEntryProps {
  organizationId: string;
  userId: string;
  currentUserRole?: string; // role gate
  currentUser?: any; // full user object for permission checks
}

type LocalEntry = {
  date: string;
  project: string;
  jobCode: string; // channel
  hours: string;
  taskDescription: string;
  status: string;
  timestamp: string;
  managerIds: string[];
};

const getWeekDays = (startDate: Date): { label: string; date: string }[] => {
  return Array.from({ length: 6 }, (_, i) => {
    const d = addDays(startDate, i);
    return {
      label: format(d, "dd-MMM"),
      date: format(d, "yyyy-MM-dd"),
    };
  });
};

const generateWeekOptionsBase = () => {
  const options: { value: string; label: string }[] = [];
  let current = startOfWeek(new Date(), { weekStartsOn: 1 });
  const startLimit = new Date(2025, 0, 1);

  while (isAfter(current, startLimit) || format(current, "yyyy-MM-dd") === format(startOfWeek(startLimit, { weekStartsOn: 1 }), "yyyy-MM-dd")) {
    const end = addDays(current, 5); // Mon to Sat
    const label = `${format(current, "dd-MMM")} — ${format(end, "dd-MMM yyyy")}`;
    options.push({ value: current.toISOString(), label });
    current = subWeeks(current, 1);
  }
  return options;
};

const TimeSheetEntry: React.FC<TimeSheetEntryProps> = ({
  organizationId,
  userId,
  currentUserRole,
  currentUser,
}) => {
  const { user: authUser } = useAuth();
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);

  // Compute timesheet permissions
  const tsPerms = React.useMemo(
    () => getTimesheetPermissions(currentUser),
    [currentUser]
  );
  /* ------------------- UI Tabs ------------------- */
  const [selectedTab, setSelectedTab] = useState<"new" | "rejected" | "history">("new");

  /* ------------------- Firestore Data ------------------- */
  const [projects, setProjects] = useState<{ id: string; title: string }[]>([]);
  const [channels, setChannels] = useState<string[]>([]);
  const [managers, setManagers] = useState<{ id: string; name: string }[]>([]);
  const [assignedProjects, setAssignedProjects] = useState<{ id: string; title: string }[]>([]);

  /* ------------------- Local State ------------------- */
  const [localTimesheets, setLocalTimesheets] = useState<LocalEntry[]>([]);
  const [rejectedTimesheets, setRejectedTimesheets] = useState<any[]>([]);
  const [historyTimesheets, setHistoryTimesheets] = useState<any[]>([]);

  /* ------------------- Add Form ------------------- */
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [date, setDate] = useState("");
  const [project, setProject] = useState<string>("");
  const [jobCode, setJobCode] = useState("");
  const [hours, setHours] = useState("");
  const [taskDescription, setTaskDescription] = useState("");
  const [managerId, setManagerId] = useState("");
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /* ------------------- Edit Form ------------------- */
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<any | null>(null);
  const [editDate, setEditDate] = useState("");
  const [editProject, setEditProject] = useState("");
  const [editJobCode, setEditJobCode] = useState("");
  const [editHours, setEditHours] = useState("");
  const [editTask, setEditTask] = useState("");
  const [editManager, setEditManager] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  /* ------------------- Task Tooltip ------------------- */
  const [tooltip, setTooltip] = useState<{ text: string; x: number; y: number } | null>(null);

  const [channelsForEdit, setChannelsForEdit] = useState<string[]>([]);

  /* ------------------- Weekly Mode State ------------------- */
  type WeeklyRow = {
    project: string;
    jobCode: string;
    taskDescription: string;
    hours: Record<string, string>;
    channels: string[];
  };
  const [selectedWeekStartStr, setSelectedWeekStartStr] = useState<string>(() => {
    return startOfWeek(new Date(), { weekStartsOn: 1 }).toISOString();
  });

  const selectedWeekStart = useMemo(() => {
    return selectedWeekStartStr === "ALL" ? startOfWeek(new Date(), { weekStartsOn: 1 }) : new Date(selectedWeekStartStr);
  }, [selectedWeekStartStr]);

  // Helper to get formatted week start key for storage/lookup
  const getWeekKey = useCallback((date: Date) => format(date, "yyyy-MM-dd"), []);

  // Initialize Weekly states from a keyed master storage (per-user)
  const [weeklyRows, setWeeklyRows] = useState<WeeklyRow[]>([]);
  const [weeklyManagerId, setWeeklyManagerId] = useState("");
  const [weeklySaving, setWeeklySaving] = useState(false);
  const [weeklySavedMode, setWeeklySavedMode] = useState(true);
  const [activeField, setActiveField] = useState<"project" | "costCenter" | "description" | null>(null);

  /* ------------------- Rejected Local States ------------------- */
  const [rejectedManagerId, setRejectedManagerId] = useState("");
  const [rejectedSaving, setRejectedSaving] = useState(false);
  const [rejectedSavedMode, setRejectedSavedMode] = useState(true);
  const [editingRejectedEntries, setEditingRejectedEntries] = useState<any[]>([]);


  /* ------------------- Leave State ------------------- */
  const [approvedLeaves, setApprovedLeaves] = useState<{ from: string, to: string, type: string }[]>([]);

  // Fetch approved leaves for the current user
  useEffect(() => {
    if (!organizationId || !userId) return;
    const fetchLeaves = async () => {
      try {
        const entries = await fetchLeaveEntries(organizationId, userId);
        const approved = entries
          .filter((e: any) => e.approvalStatus === "Approved" || e.approvalStatus === "approved")
          .map((e: any) => {
            // Priority: Reason (Sick/Casual/Comp Off) > Leave Type (Paid/Unpaid)
            let typeLabel = e.reason || e.leaveType || "Leave";
            if (typeLabel.toLowerCase() === "paid" && e.reason) {
              typeLabel = e.reason;
            }
            return { from: e.fromDate, to: e.toDate, type: typeLabel };
          });
        setApprovedLeaves(approved);
      } catch (err) {
        console.error("Error fetching leaves:", err);
      }
    };
    fetchLeaves();
  }, [organizationId, userId]);

  const getLeaveType = (dateStr: string) => {
    if (!dateStr) return null;
    const targetDate = new Date(dateStr).getTime();
    const leave = approvedLeaves.find(l => {
      const from = new Date(l.from).getTime();
      const to = new Date(l.to).getTime();
      return targetDate >= from && targetDate <= to;
    });
    return leave ? leave.type : null;
  };

  const isLeaveDay = (dateStr: string) => !!getLeaveType(dateStr);

  /* ------------------- Holiday State ------------------- */
  const [holidays, setHolidays] = useState<Record<string, string>>({});

  // Fetch organization holidays (same source as Leave Calendar)
  useEffect(() => {
    if (!organizationId) return;
    const fetchHolidays = async () => {
      try {
        const holidaysSnap = await getDocs(
          collection(db, "organization", organizationId, "holidays")
        );
        const map: Record<string, string> = {};
        holidaysSnap.forEach((d) => {
          const data: any = d.data();
          const key = typeof data?.date === "string" ? data.date : d.id;
          if (key) map[key] = data?.reason || "Holiday";
        });
        setHolidays(map);
      } catch (err) {
        console.error("Error fetching holidays:", err);
      }
    };
    fetchHolidays();
  }, [organizationId]);

  const getHolidayReason = (dateStr: string) => {
    if (!dateStr) return null;
    return holidays[dateStr] || null;
  };

  const isHolidayDay = (dateStr: string) => !!getHolidayReason(dateStr);

  // Sync state with selected week change
  useEffect(() => {
    if (!userId) return;
    const masterData = JSON.parse(localStorage.getItem(`weekly_timesheet_master_${userId}`) || "{}");
    const weekKey = getWeekKey(selectedWeekStart);

    if (masterData[weekKey]) {
      setWeeklyRows(masterData[weekKey].rows || [{ project: "", jobCode: "", taskDescription: "", hours: {}, channels: [] }]);
      setWeeklyManagerId(masterData[weekKey].managerId || "");
    } else {
      setWeeklyRows([{ project: "", jobCode: "", taskDescription: "", hours: {}, channels: [] }]);
      setWeeklyManagerId("");
    }
    // Default to true (Safe Mode) when switching weeks
    setWeeklySavedMode(true);
  }, [selectedWeekStart, userId, getWeekKey]);

  // Sync Weekly states to master localStorage on every change
  useEffect(() => {
    if (!userId || weeklyRows.length === 0) return;
    const masterData = JSON.parse(localStorage.getItem(`weekly_timesheet_master_${userId}`) || "{}");
    const weekKey = getWeekKey(selectedWeekStart);

    masterData[weekKey] = { rows: weeklyRows, managerId: weeklyManagerId };
    localStorage.setItem(`weekly_timesheet_master_${userId}`, JSON.stringify(masterData));
  }, [weeklyRows, weeklyManagerId, selectedWeekStart, userId, getWeekKey]);

  // Derived state: check if any data for this week already exists in history
  const weekDays = React.useMemo(() => getWeekDays(selectedWeekStart), [selectedWeekStart]);

  const weekLeaves = React.useMemo(() => {
    const leavesMap: Record<string, string> = {};
    weekDays.forEach(d => {
      const type = getLeaveType(d.date);
      if (type) leavesMap[d.date] = type;
    });
    const uniqueTypes = Array.from(new Set(Object.values(leavesMap)));
    return uniqueTypes.map(type => ({
      type,
      days: weekDays.filter(d => leavesMap[d.date] === type).map(d => d.date)
    }));
  }, [selectedWeekStart, approvedLeaves, weekDays]);

  const weekHolidays = React.useMemo(() => {
    const holidayMap: Record<string, string> = {};
    weekDays.forEach(d => {
      // A leave already occupies its own row for that day — don't duplicate it.
      if (getLeaveType(d.date)) return;
      const reason = getHolidayReason(d.date);
      if (reason) holidayMap[d.date] = reason;
    });
    const uniqueReasons = Array.from(new Set(Object.values(holidayMap)));
    return uniqueReasons.map(reason => ({
      reason,
      days: weekDays.filter(d => holidayMap[d.date] === reason).map(d => d.date)
    }));
  }, [selectedWeekStart, holidays, approvedLeaves, weekDays]);

  const isWeekSubmitted = useCallback(() => {
    return historyTimesheets.some(h => weekDays.some(wd => wd.date === h.date));
  }, [weekDays, historyTimesheets]);


  // Sync Weekly states to localStorage (per-user)
  useEffect(() => {
    if (userId) {
      localStorage.setItem(`weekly_timesheet_rows_${userId}`, JSON.stringify(weeklyRows));
    }
  }, [weeklyRows, userId]);

  useEffect(() => {
    if (userId) {
      localStorage.setItem(`weekly_manager_id_${userId}`, weeklyManagerId);
    }
  }, [weeklyManagerId, userId]);

  useEffect(() => {
    const fetchChannelsForEdit = async () => {
      if (!isEditOpen || !editProject) {
        setChannelsForEdit([]);
        return;
      }

      // Find the project by title from assigned projects, or fallback to all projects
      let proj = assignedProjects.find((p) => p.title === editProject);

      // If assignedProjects is not populated yet, try to find from all projects
      if (!proj && projects.length > 0) {
        proj = projects.find((p) => p.title === editProject);
      }

      if (!proj) {
        setChannelsForEdit([]);
        return;
      }

      const snap = await getDocs(
        collection(
          db,
          `organization/${organizationId}/projects/${proj.id}/channels`
        )
      );

      // Build a map of channel document ID → channel name
      const channelIdToName: Record<string, string> = {};
      snap.docs.forEach((d) => {
        channelIdToName[d.id] = d.data().name;
      });
      const allChannelNames = Object.values(channelIdToName);

      // Get user's assigned channels for this project
      const orgDoc = await getDoc(doc(db, `organization/${organizationId}`));
      if (orgDoc.exists()) {
        const { members = [] } = orgDoc.data();
        const user = members.find((m: any) => m.id === userId);

        if (user && user.assignedChannels && user.assignedChannels[proj.id] && user.assignedChannels[proj.id].length > 0) {
          // assignedChannels stores channel IDs — map them to names
          const assignedChannelIds: string[] = user.assignedChannels[proj.id];
          const filteredChannels = assignedChannelIds
            .map((id) => channelIdToName[id])
            .filter(Boolean);
          setChannelsForEdit(filteredChannels.length > 0 ? filteredChannels : allChannelNames);
        } else {
          setChannelsForEdit(allChannelNames); // Fallback to all channels if no assignment found
        }
      } else {
        setChannelsForEdit(allChannelNames);
      }
    };
    fetchChannelsForEdit();
  }, [isEditOpen, editProject, organizationId, assignedProjects, projects, userId]);

  /* ------------------------------------------------------ */
  // Fetch Projects
  useEffect(() => {
    const fetchProjects = async () => {
      const snap = await getDocs(
        collection(db, `organization/${organizationId}/projects`)
      );
      setProjects(snap.docs.map((d) => ({ id: d.id, title: d.data().name })));
    };
    if (organizationId) fetchProjects();
  }, [organizationId]);

  // Fetch Assigned Projects for current user
  useEffect(() => {
    const fetchAssignedProjects = async () => {
      if (!organizationId || !userId) return;

      const orgDoc = await getDoc(doc(db, `organization/${organizationId}`));
      if (orgDoc.exists()) {
        const { members = [] } = orgDoc.data();
        const user = members.find((m: any) => m.id === userId);

        if (user && user.assignedProjects && user.assignedProjects.length > 0) {
          // Filter projects to only show assigned ones
          const assignedProjIds = user.assignedProjects;
          const allProjects = projects.filter(p => assignedProjIds.includes(p.id));
          setAssignedProjects(allProjects);
        } else {
          // Fallback to all projects if no assignments found
          setAssignedProjects(projects);
        }
      } else {
        setAssignedProjects(projects);
      }
    };

    if (organizationId && userId && projects.length > 0) {
      fetchAssignedProjects();
    }
  }, [organizationId, userId, projects]);

  // Fetch Channels when project changes (used by Add modal + current implementation)
  useEffect(() => {
    const fetchChannels = async () => {
      if (!project) {
        setChannels([]);
        return;
      }

      // First try to find the project from all projects (more reliable)
      let proj = projects.find((p) => p.title === project);

      // If not found in all projects, try assigned projects as fallback
      if (!proj && assignedProjects.length > 0) {
        proj = assignedProjects.find((p) => p.title === project);
      }

      if (!proj) {
        setChannels([]);
        return;
      }

      try {
        const snap = await getDocs(
          collection(
            db,
            `organization/${organizationId}/projects/${proj.id}/channels`
          )
        );

        // Build map of channelId -> name
        const channelIdToName = new Map<string, string>();
        snap.docs.forEach((d) => {
          const data = d.data() as any;
          channelIdToName.set(d.id, data.name);
        });

        // Get user's assigned channels for this project
        const orgDoc = await getDoc(doc(db, `organization/${organizationId}`));
        if (orgDoc.exists()) {
          const { members = [] } = orgDoc.data();
          const user = members.find((m: any) => m.id === userId);

          if (
            user &&
            user.assignedChannels &&
            user.assignedChannels[proj.id] &&
            Array.isArray(user.assignedChannels[proj.id]) &&
            user.assignedChannels[proj.id].length > 0
          ) {
            const assignedChannelIds: string[] = user.assignedChannels[proj.id];
            const filteredNames = assignedChannelIds
              .map((id: string) => channelIdToName.get(id))
              .filter((name): name is string => Boolean(name));
            setChannels(filteredNames);
          } else {
            // No assignment for this project → show none
            setChannels([]);
          }
        } else {
          setChannels([]);
        }
      } catch (error) {
        console.error("Error fetching channels:", error);
        setChannels([]);
      }
    };

    // Only fetch if we have the necessary data
    if (project && organizationId && (projects.length > 0 || assignedProjects.length > 0)) {
      fetchChannels();
    }
  }, [project, organizationId, projects, assignedProjects, userId]);

  // Reset form when Add modal opens
  useEffect(() => {
    if (isAddOpen) {
      resetForm();
    }
  }, [isAddOpen]);

  // Fetch Managers
  useEffect(() => {
    const fetchManagers = async () => {
      const orgDoc = await getDoc(doc(db, `organization/${organizationId}`));
      if (orgDoc.exists()) {
        const { members = [] } = orgDoc.data();
        const mgrs = members
          .filter((m: any) => {
            const role = (m.role || "").toLowerCase().replace(/\s+/g, "");
            const isManager = role === "manager" || role === "managingdirector";
            const hasApprovalStatus = (m.approvalStatus || "").toLowerCase() === "yes";
            return (
              (isManager || hasApprovalStatus) &&
              m.id !== userId &&
              m.id !== authUser?.uid
            );
          })
          .map((m: any) => ({
            id: m.id,
            name: `${m.firstName} ${m.lastName}`,
          }));
        setManagers(mgrs);
      }
    };
    if (organizationId) fetchManagers();
  }, [organizationId]);

  // Load Local Timesheets
  const loadLocal = useCallback(async () => {
    const orgDoc = await getDoc(doc(db, `organization/${organizationId}`));
    if (orgDoc.exists()) {
      const { members = [] } = orgDoc.data();
      const user = members.find((m: any) => m.id === userId);
      const raw = user?.tempTimesheet || [];
      setLocalTimesheets(
        [...raw].sort((a, b) => {
          const dateA = new Date(a.date).getTime();
          const dateB = new Date(b.date).getTime();
          if (dateA !== dateB) return dateB - dateA;
          return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
        })
      );
    }
  }, [organizationId, userId]);

  useEffect(() => {
    if (organizationId && userId) loadLocal();
  }, [organizationId, userId, loadLocal]);


  // Load Rejected Timesheets
  const loadRejected = useCallback(async () => {
    if (!organizationId || !userId) return;
    try {
      const entries: any[] = [];
      const seenKeys = new Set<string>();

      // 1. Fetch from weekly architecture: TimeSheetsData/{userId}/weeks/{weekMonday}
      const tsColRef = collection(db, "organization", organizationId, "TimeSheetsData", userId, "weeks");
      const newSnap = await getDocs(tsColRef);
      newSnap.docs.forEach(wkDoc => {
        const wkData = wkDoc.data();
        const weekEntries = wkData.entries || [];
        weekEntries.forEach((entry: any) => {
          const status = (entry.status || "").toLowerCase();
          if (status === "rejected" || status === "reject") {
            const key = `${entry.date}-${entry.project}-${entry.hours}-${entry.timestamp}`;
            if (!seenKeys.has(key)) {
              entries.push({ ...entry, id: entry.id || wkDoc.id, status: entry.status || "rejected" });
              seenKeys.add(key);
            }
          }
        });
      });

      // 2. Fetch from legacy structure
      const oldRef = doc(db, "organization", organizationId, "timesheets", userId);
      const oldSnap = await getDoc(oldRef);
      if (oldSnap.exists()) {
        const legacyEntries = oldSnap.data().entries || [];
        legacyEntries.forEach((entry: any, index: number) => {
          const status = (entry.status || "").toLowerCase();
          if (status === "rejected" || status === "reject") {
            const key = `${entry.date}-${entry.project}-${entry.hours}-${entry.timestamp}`;
            if (!seenKeys.has(key)) {
              entries.push({ ...entry, id: `legacy-rejected-${index}`, status: entry.status || "rejected" });
            }
          }
        });
      }

      setRejectedTimesheets(entries.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
    } catch (err) {
      console.error("Error loading rejected:", err);
    }
  }, [organizationId, userId]);

  useEffect(() => {
    loadRejected();
  }, [loadRejected]);

  // Pre-select manager for Rejected tab
  useEffect(() => {
    if (selectedTab === "rejected" && rejectedTimesheets.length > 0 && !rejectedManagerId) {
      const first = rejectedTimesheets[0];
      if (first.managerIds && first.managerIds.length > 0) {
        setRejectedManagerId(first.managerIds[0]);
      }
    }
  }, [selectedTab, rejectedTimesheets, rejectedManagerId]);

  // Load History Timesheets (all entries)
  const loadHistory = useCallback(async () => {
    if (!organizationId || !userId) return;
    try {
      const entries: any[] = [];
      const seenKeys = new Set<string>();

      // 1. Fetch from weekly architecture: TimeSheetsData/{userId}/weeks/{weekMonday}
      const newTsColRef = collection(db, "organization", organizationId, "TimeSheetsData", userId, "weeks");
      const newSnap = await getDocs(newTsColRef);
      newSnap.docs.forEach(wkDoc => {
        const wkData = wkDoc.data();
        const weekEntries = wkData.entries || [];
        weekEntries.forEach((entry: any) => {
          const status = (entry.status || "").toLowerCase();
          if (["approved", "approve", "rejected", "reject", "pending"].includes(status) || status === "") {
            const key = `${entry.date}-${entry.project}-${entry.hours}-${entry.timestamp}`;
            if (!seenKeys.has(key)) {
              entries.push({ ...entry, id: entry.id || wkDoc.id, status: entry.status || "pending" });
              seenKeys.add(key);
            }
          }
        });
      });

      // 2. Fetch from legacy structure
      const oldRef = doc(db, "organization", organizationId, "timesheets", userId);
      const oldSnap = await getDoc(oldRef);
      if (oldSnap.exists()) {
        const legacyEntries = oldSnap.data().entries || [];
        legacyEntries.forEach((entry: any, index: number) => {
          const status = (entry.status || "").toLowerCase();
          if (["approved", "approve", "rejected", "reject", "pending"].includes(status) || status === "") {
            const key = `${entry.date}-${entry.project}-${entry.hours}-${entry.timestamp}`;
            if (!seenKeys.has(key)) {
              entries.push({ ...entry, id: `legacy-history-${index}`, status: entry.status || "pending" });
            }
          }
        });
      }

      setHistoryTimesheets(entries.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
    } catch (err) {
      console.error("Error loading history:", err);
    }
  }, [organizationId, userId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  /* ------------------- Helpers ------------------- */
  const resetForm = () => {
    const d = new Date();
    setDate(format(d, "yyyy-MM-dd"));
    setProject("");
    setJobCode("");
    setHours("");
    setTaskDescription("");
    setManagerId("");
  };

  const handleAdd = async () => {
    if (
      !date ||
      !project ||
      !jobCode ||
      !hours ||
      !taskDescription ||
      !managerId
    ) {
      showToast({ title: "Please fill all fields", status: "warning" });
      return;
    }
    setSaving(true);
    const newEntry: LocalEntry = {
      date,
      project,
      jobCode,
      hours,
      taskDescription,
      status: "pending",
      timestamp: new Date().toISOString(),
      managerIds: [managerId],
    };
    try {
      const orgRef = doc(db, `organization/${organizationId}`);
      const orgSnap = await getDoc(orgRef);
      if (!orgSnap.exists()) throw new Error("Org doc missing");
      const data = orgSnap.data();
      const members = data.members || [];
      const idx = members.findIndex((m: any) => m.id === userId);
      if (idx === -1) throw new Error("User missing in org members");
      const user = members[idx];
      const updated = [...(user.tempTimesheet || []), newEntry];
      // Sort updated entries: Date desc, Timestamp desc
      const sorted = [...updated].sort((a, b) => {
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        if (dateA !== dateB) return dateB - dateA;
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      });
      members[idx] = { ...user, tempTimesheet: sorted };
      await updateDoc(orgRef, { members });
      setLocalTimesheets(sorted);
      setIsAddOpen(false);
      resetForm();
    } catch (err: any) {
      console.error(err);
      showToast({ title: "Failed to add timesheet", status: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLocal = async (index: number) => {
    try {
      const orgRef = doc(db, `organization/${organizationId}`);
      const orgSnap = await getDoc(orgRef);
      if (!orgSnap.exists()) return;
      const members = orgSnap.data().members || [];
      const idx = members.findIndex((m: any) => m.id === userId);
      if (idx === -1) return;
      const user = members[idx];
      const updated = [...(user.tempTimesheet || [])];
      updated.splice(index, 1);
      members[idx] = { ...user, tempTimesheet: updated };
      await updateDoc(orgRef, { members });
      setLocalTimesheets(updated);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = async () => {
    if (localTimesheets.length === 0) return;
    setSubmitting(true);
    try {
      // Group entries by week Monday (matching mobile app weekly architecture)
      const entriesByWeek: Record<string, any[]> = {};
      for (const entry of localTimesheets) {
        const entryDate = new Date(entry.date);
        const weekMonday = format(startOfWeek(entryDate, { weekStartsOn: 1 }), 'yyyy-MM-dd');
        if (!entriesByWeek[weekMonday]) entriesByWeek[weekMonday] = [];
        entriesByWeek[weekMonday].push(entry);
      }

      // Write to weekly architecture: TimeSheetsData/{userId}/weeks/{weekMonday}
      for (const [weekMonday, weekEntries] of Object.entries(entriesByWeek)) {
        const tsDocRef = doc(db, "organization", organizationId, "TimeSheetsData", userId, "weeks", weekMonday);
        const existingSnap = await getDoc(tsDocRef);
        const existingEntries = existingSnap.exists() ? (existingSnap.data().entries || []) : [];
        const newEntries = weekEntries.map((entry: any, idx: number) => ({
          ...entry,
          id: `wk-${weekMonday}-${Date.now()}-${idx}`,
        }));
        await setDoc(tsDocRef, {
          userId,
          weekMonday,
          entries: [...existingEntries, ...newEntries],
        }, { merge: true });
      }

      // clear local in org doc
      const orgRef = doc(db, `organization/${organizationId}`);
      const orgSnap = await getDoc(orgRef);
      if (orgSnap.exists()) {
        const data = orgSnap.data();
        const members = data.members || [];
        const idx = members.findIndex((m: any) => m.id === userId);
        if (idx !== -1) {
          members[idx] = { ...members[idx], tempTimesheet: [] };
          await updateDoc(orgRef, { members });
        }
      }
      setLocalTimesheets([]);
      // Refresh history and rejected immediately
      await loadHistory();
      await loadRejected();
      showToast({ title: "Timesheets submitted for approval", status: "success" });
    } catch (err) {
      console.error(err);
      showToast({ title: "Failed to submit", status: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRejectedSubmit = async () => {
    const weekDateStrings = weekDays.map(wd => wd.date);
    const entriesToUpdate = selectedWeekStartStr === "ALL"
      ? rejectedTimesheets
      : rejectedTimesheets.filter(e => weekDateStrings.includes(e.date));

    if (entriesToUpdate.length === 0) return;
    if (!rejectedManagerId) { showToast({ title: "Please select a Manager.", status: "warning" }); return; }

    setRejectedSaving(true);
    try {
      for (const entry of entriesToUpdate) {
        const updatedEntry = {
          ...entry,
          project: entry.project,
          jobCode: entry.jobCode || entry.channel,
          hours: entry.hours,
          taskDescription: entry.taskDescription,
          status: "pending",
          managerIds: [rejectedManagerId],
          timestamp: new Date().toISOString()
        };

        // Find and update in weekly architecture: TimeSheetsData/{userId}/weeks/{weekMonday}
        const tsColRef = collection(db, "organization", organizationId, "TimeSheetsData", userId, "weeks");
        const snap = await getDocs(tsColRef);
        for (const wkDoc of snap.docs) {
          const wkData = wkDoc.data();
          const entries = wkData.entries || [];
          const eIdx = entries.findIndex((e: any) => e.id === entry.id);
          if (eIdx !== -1) {
            entries[eIdx] = updatedEntry;
            await setDoc(wkDoc.ref, { ...wkData, entries }, { merge: true });
            break;
          }
        }
      }

      showToast({ title: "Rejected timesheets resubmitted successfully.", status: "success" });
      setRejectedSavedMode(true);
      await loadRejected();
      await loadHistory();
    } catch (err) {
      console.error(err);
      showToast({ title: "Failed to submit rejected timesheets.", status: "error" });
    } finally {
      setRejectedSaving(false);
    }
  };

  const openEditModal = (entry: any) => {
    setEditTarget(entry);
    setEditDate(entry.date);
    setEditProject(entry.project);
    setEditJobCode(entry.jobCode);
    setEditHours(String(entry.hours));
    setEditTask(entry.taskDescription);
    setEditManager(
      entry.managerIds && entry.managerIds.length > 0 ? entry.managerIds[0] : ""
    );
    setIsEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (
      !editDate ||
      !editProject ||
      !editJobCode ||
      !editHours ||
      !editTask ||
      !editManager
    ) {
      showToast({ title: "Fill all fields", status: "warning" });
      return;
    }
    setEditSaving(true);
    const updated: LocalEntry = {
      date: editDate,
      project: editProject,
      jobCode: editJobCode,
      hours: editHours,
      taskDescription: editTask,
      status: "pending",
      timestamp: new Date().toISOString(),
      managerIds: [editManager],
    };

    try {
      // 1) Remove from weekly architecture: TimeSheetsData/{userId}/weeks/{weekMonday}
      if (editTarget.id) {
        const tsColRef = collection(db, "organization", organizationId, "TimeSheetsData", userId, "weeks");
        const snap = await getDocs(tsColRef);
        for (const wkDoc of snap.docs) {
          const wkData = wkDoc.data();
          const entries = wkData.entries || [];
          const eIdx = entries.findIndex((e: any) => e.id === editTarget.id);
          if (eIdx !== -1) {
            entries.splice(eIdx, 1);
            await setDoc(wkDoc.ref, { ...wkData, entries }, { merge: true });
            break;
          }
        }
      }

      // 2) Add to local tempTimesheet
      const orgRef = doc(db, `organization/${organizationId}`);
      const orgSnap = await getDoc(orgRef);
      if (orgSnap.exists()) {
        const data = orgSnap.data();
        const members = data.members || [];
        const idx = members.findIndex((m: any) => m.id === userId);
        if (idx !== -1) {
          const user = members[idx];
          const newLocal = [...(user.tempTimesheet || []), updated];
          // Sort updated entries: Date desc, Timestamp desc
          const sorted = [...newLocal].sort((a, b) => {
            const dateA = new Date(a.date).getTime();
            const dateB = new Date(b.date).getTime();
            if (dateA !== dateB) return dateB - dateA;
            return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
          });
          members[idx] = { ...user, tempTimesheet: sorted };
          await updateDoc(orgRef, { members });
          setLocalTimesheets(sorted);
        }
      }

      // 3) Remove from UI rejected list
      setRejectedTimesheets((prev) => prev.filter((e) => e !== editTarget));
      setIsEditOpen(false);
    } catch (err) {
      console.error(err);
      showToast({ title: "Failed to save edit", status: "error" });
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeleteRejected = async (entry: any) => {
    try {
      if (entry.id) {
        // Delete from weekly architecture: TimeSheetsData/{userId}/weeks/{weekMonday}
        const tsColRef = collection(db, "organization", organizationId, "TimeSheetsData", userId, "weeks");
        const snap = await getDocs(tsColRef);
        for (const wkDoc of snap.docs) {
          const wkData = wkDoc.data();
          const entries = wkData.entries || [];
          const idx = entries.findIndex((e: any) => e.id === entry.id);
          if (idx !== -1) {
            entries.splice(idx, 1);
            await setDoc(wkDoc.ref, { ...wkData, entries }, { merge: true });
            break;
          }
        }
      }
      // Refresh both states to ensure consistency
      await loadRejected();
      await loadHistory();
    } catch (err) {
      console.error(err);
      showToast({ title: "Failed to delete rejected entry", status: "error" });
    }
  };

  /* ------------------- Weekly helpers ------------------- */
  const generateWeekOptions = useCallback(() => {
    const base = generateWeekOptionsBase();
    if (selectedTab === 'history' || selectedTab === 'rejected') {
      return [{ value: "ALL", label: "All Timesheets" }, ...base];
    }
    return base;
  }, [selectedTab]);


  const fetchChannelsForWeeklyRow = async (projectTitle: string): Promise<string[]> => {
    if (!projectTitle) return [];
    let proj = projects.find((p) => p.title === projectTitle) ||
      assignedProjects.find((p) => p.title === projectTitle);
    if (!proj) return [];
    try {
      const snap = await getDocs(collection(db, `organization/${organizationId}/projects/${proj.id}/channels`));
      const channelIdToName: Record<string, string> = {};
      snap.docs.forEach((d) => { channelIdToName[d.id] = d.data().name; });
      const orgDoc = await getDoc(doc(db, `organization/${organizationId}`));
      if (orgDoc.exists()) {
        const { members = [] } = orgDoc.data();
        const u = members.find((m: any) => m.id === userId);
        if (u?.assignedChannels?.[proj!.id]?.length > 0) {
          const names = (u.assignedChannels[proj!.id] as string[]).map((id) => channelIdToName[id]).filter(Boolean);
          return names.length > 0 ? names : Object.values(channelIdToName);
        }
      }
      return Object.values(channelIdToName);
    } catch { return []; }
  };

  const updateWeeklyRow = (idx: number, field: keyof WeeklyRow | string, value: any) => {
    setWeeklyRows((prev) => {
      const updated = [...prev];
      if (field === "hours") {
        updated[idx] = { ...updated[idx], hours: { ...updated[idx].hours, ...value } };
      } else {
        updated[idx] = { ...updated[idx], [field]: value };
      }
      return updated;
    });
  };

  const addWeeklyRow = () => {
    setWeeklyRows((prev) => [
      ...prev,
      { project: "", jobCode: "", taskDescription: "", hours: {}, channels: [] },
    ]);
  };

  const removeWeeklyRow = (idx: number) => {
    setWeeklyRows((prev) => prev.filter((_, i) => i !== idx));
  };


  const handleWeeklySaveClick = () => {
    // Basic validation
    const weekDays = getWeekDays(selectedWeekStart);
    let hasEntries = false;
    for (const row of weeklyRows) {
      if (row.project && row.jobCode) {
        for (const day of weekDays) {
          const h = row.hours[day.date];
          if (h && !isNaN(parseFloat(h)) && parseFloat(h) > 0) {
            hasEntries = true;
            break;
          }
        }
      }
      if (hasEntries) break;
    }
    if (!hasEntries) { showToast({ title: "Please fill at least one row with project, channel, and hours.", status: "warning" }); return; }
    if (!weeklyManagerId) { showToast({ title: "Please select a Manager.", status: "warning" }); return; }

    setWeeklySavedMode(true);
  };

  const handleWeeklySubmit = async () => {
    const weekDays = getWeekDays(selectedWeekStart);
    const entries: any[] = [];
    for (const row of weeklyRows) {
      if (!row.project || !row.jobCode) continue;
      for (const day of weekDays) {
        const h = row.hours[day.date];
        if (!h || isNaN(parseFloat(h)) || parseFloat(h) <= 0) continue;
        entries.push({
          date: day.date,
          project: row.project,
          jobCode: row.jobCode,
          hours: parseFloat(h),
          taskDescription: row.taskDescription,
          status: "pending",
          timestamp: new Date().toISOString(),
          managerIds: weeklyManagerId ? [weeklyManagerId] : [],
          userId: userId
        });
      }
    }

    if (entries.length === 0) return;
    setWeeklySaving(true);
    try {
      // Group entries by week Monday (matching mobile app weekly architecture)
      const entriesByWeek: Record<string, any[]> = {};
      for (const entry of entries) {
        const entryDate = new Date(entry.date);
        const weekMonday = format(startOfWeek(entryDate, { weekStartsOn: 1 }), 'yyyy-MM-dd');
        if (!entriesByWeek[weekMonday]) entriesByWeek[weekMonday] = [];
        entriesByWeek[weekMonday].push(entry);
      }

      // Write to weekly architecture: TimeSheetsData/{userId}/weeks/{weekMonday}
      for (const [weekMonday, weekEntries] of Object.entries(entriesByWeek)) {
        const tsDocRef = doc(db, "organization", organizationId, "TimeSheetsData", userId, "weeks", weekMonday);
        const existingSnap = await getDoc(tsDocRef);
        const existingEntries = existingSnap.exists() ? (existingSnap.data().entries || []) : [];
        const newEntries = weekEntries.map((entry: any, idx: number) => ({
          ...entry,
          id: `wk-${weekMonday}-${Date.now()}-${idx}`,
        }));
        await setDoc(tsDocRef, {
          userId,
          weekMonday,
          entries: [...existingEntries, ...newEntries],
        }, { merge: true });
      }

      localStorage.removeItem("weekly_timesheet_rows");
      localStorage.removeItem("weekly_manager_id");
      setWeeklyRows([{ project: "", jobCode: "", taskDescription: "", hours: {}, channels: [] }]);
      setWeeklyManagerId("");
      setWeeklySavedMode(true); // Reset to saved mode

      // Refresh history immediately
      await loadHistory();
      await loadRejected();

      showToast({ title: "Weekly timesheet submitted successfully!", status: "success" });
    } catch (err) {
      console.error(err);
      showToast({ title: "Failed to submit weekly timesheet.", status: "error" });
    } finally {
      setWeeklySaving(false);
    }
  };

  /* ------------------- Render ------------------- */
  const excelThemeVars = {
    "--excel-border-color": isDark ? "#2E2F33" : "#D1D5DB",
    "--excel-header-bg": isDark ? "#14161B" : "#FAFBFC",
    "--excel-header-text": isDark ? "#C3C9D3" : "#374151",
    "--excel-cell-bg": isDark ? "#1A1C1F" : "#FFFFFF",
    "--excel-text-color": isDark ? "#FFFFFF" : "#000000",
    "--excel-focus-bg": isDark ? "#2A3441" : "#FFFDF5",
    "--excel-leave-bg": isDark ? "#2D2D33" : "#F3F4F6",
    "--excel-leave-text": isDark ? "#9CA3AF" : "#6B7280",
  };

  return (
    <div className="px-4" style={excelThemeVars as React.CSSProperties}>
      {/* Excel UI styles */}
      <style>{`
        .excel-table {
          border-collapse: separate !important;
          border-spacing: 0 !important;
          width: 100%;
          table-layout: auto;
          margin-bottom: 1rem;
          border-top: 1px solid var(--excel-border-color) !important;
          border-left: 1px solid var(--excel-border-color) !important;
        }
        .excel-table th {
          border-bottom: 1px solid var(--excel-border-color) !important;
          border-right: 1px solid var(--excel-border-color) !important;
          padding: 8px 12px !important;
          font-size: 11px !important;
          font-weight: 600 !important;
          background-color: var(--excel-header-bg) !important;
          color: var(--excel-header-text) !important;
          text-align: left;
        }
        .excel-table th.text-center {
          text-align: center !important;
        }
        .excel-table td {
          border-bottom: 1px solid var(--excel-border-color) !important;
          border-right: 1px solid var(--excel-border-color) !important;
          padding: 0 !important;
          height: 38px;
          background-color: var(--excel-cell-bg);
          vertical-align: middle;
        }
        .excel-cell-text {
          width: 100%;
          height: 100%;
          padding: 8px 12px;
          font-size: 12px;
          display: flex;
          align-items: center;
          color: var(--excel-text-color);
          font-weight: 500;
        }
        .excel-cell-text-center {
          justify-content: center;
          text-align: center;
        }
        .excel-input {
          width: 100% !important;
          height: 100% !important;
          border: none !important;
          outline: none !important;
          background: transparent !important;
          padding: 8px 12px !important;
          font-size: 12px !important;
          color: var(--excel-text-color) !important;
          border-radius: 0px !important;
          box-shadow: none !important;
          text-align: center;
          font-weight: 600;
        }
        .excel-input:focus {
          background-color: var(--excel-focus-bg) !important;
          box-shadow: inset 0 0 0 2px #B59143 !important;
        }
        /* Hide number spin buttons */
        .excel-input[type=number]::-webkit-inner-spin-button, 
        .excel-input[type=number]::-webkit-outer-spin-button { 
          -webkit-appearance: none; 
          margin: 0; 
        }
        .excel-input[type=number] {
          -moz-appearance: textfield;
        }
        .excel-textarea {
          width: 100% !important;
          height: 100% !important;
          border: none !important;
          outline: none !important;
          background: transparent !important;
          padding: 8px 12px !important;
          font-size: 12px !important;
          color: var(--excel-text-color) !important;
          border-radius: 0px !important;
          box-shadow: none !important;
          resize: none !important;
          display: flex;
          align-items: center;
        }
        .excel-textarea:focus {
          background-color: var(--excel-focus-bg) !important;
          box-shadow: inset 0 0 0 2px #B59143 !important;
        }
        .excel-dropdown {
          width: 100% !important;
          height: 100% !important;
          border: none !important;
          border-radius: 0px !important;
          box-shadow: none !important;
          background: transparent !important;
          padding: 8px 12px !important;
          font-size: 12px !important;
          color: var(--excel-text-color) !important;
        }
        .excel-dropdown:focus, .excel-dropdown:focus-within {
          background-color: var(--excel-focus-bg) !important;
          box-shadow: inset 0 0 0 2px #B59143 !important;
        }
        
        /* Custom dropdown list inside portals override styling */
        div[style*="z-index: 9999"] button[type="button"] {
          white-space: normal !important;
          word-break: break-word !important;
          line-height: 1.4 !important;
          padding-top: 10px !important;
          padding-bottom: 10px !important;
          height: auto !important;
        }

        /* Excel scrollbar and container styles */
        .excel-table-container {
          max-height: 100%;
          overflow-x: auto !important;
          overflow-y: auto !important;
        }
        .excel-table-container::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        .excel-table-container::-webkit-scrollbar-track {
          background: transparent;
        }
        .excel-table-container::-webkit-scrollbar-thumb {
          background: ${isDark ? '#2E2F33' : '#E5E7EB'};
          border-radius: 4px;
        }
        .excel-table-container::-webkit-scrollbar-thumb:hover {
          background: #B59143;
        }

        /* Sticky Columns for Spreadsheet grid */
        .excel-sticky-l1 {
          position: sticky !important;
          left: 0 !important;
          z-index: 4 !important;
        }
        .excel-sticky-r1 {
          position: sticky !important;
          right: 0 !important;
          z-index: 4 !important;
        }
        .excel-sticky-r2 {
          position: sticky !important;
          right: 65px !important;
          z-index: 4 !important;
        }

        /* Freeze Project column on desktop only (width >= 1024px) */
        @media (min-width: 1024px) {
          .excel-sticky-l2 {
            position: sticky !important;
            left: 48px !important;
            z-index: 4 !important;
          }
          th.excel-sticky-l2 {
            background-color: var(--excel-header-bg) !important;
            z-index: 10 !important;
          }
          td.excel-sticky-l2 {
            background-color: var(--excel-cell-bg) !important;
          }
          tr.excel-leave-row td.excel-sticky-l2 {
            background-color: var(--excel-leave-bg) !important;
          }
        }

        th.excel-sticky-l1, th.excel-sticky-r1, th.excel-sticky-r2 {
          background-color: var(--excel-header-bg) !important;
          z-index: 10 !important;
        }
        td.excel-sticky-l1, td.excel-sticky-r1, td.excel-sticky-r2 {
          background-color: var(--excel-cell-bg) !important;
        }
        tr.excel-leave-row td.excel-sticky-l1,
        tr.excel-leave-row td.excel-sticky-r1,
        tr.excel-leave-row td.excel-sticky-r2 {
          background-color: var(--excel-leave-bg) !important;
        }

        /* Frozen boundaries visual feedback dividers */
        @media (min-width: 1024px) {
          .excel-sticky-l2 {
            border-right: 2px solid var(--excel-border-color) !important;
          }
        }
        @media (max-width: 1023px) {
          .excel-sticky-l1 {
            border-right: 2px solid var(--excel-border-color) !important;
          }
        }
        .excel-sticky-divider-left {
          border-left: 2px solid var(--excel-border-color) !important;
        }
      `}</style>

      {/* Top Row: Dropdown Filter left, Tabs Dropdown right */}
      <div className="flex flex-row items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className="w-[200px] timesheet-dropdown-wrap">
            <CustomDropdown
              value={selectedWeekStartStr}
              onChange={(val) => setSelectedWeekStartStr(val)}
              isDark={isDark}
              placeholder="Select Week"
              className="h-9 text-sm w-full"
              options={generateWeekOptions()}
            />
          </div>
          {selectedTab === "new" && !isWeekSubmitted() && tsPerms.canCreate && (
            <button
              onClick={addWeeklyRow}
              title="Add row"
              className={`flex items-center justify-center w-9 h-9 rounded-lg border transition-all shadow-sm focus:outline-none ${isDark ? 'border-[#B59143] text-[#FBD282] hover:bg-[#B59143]/10' : 'border-[#B59143] text-[#B59143] hover:bg-[#FFF7E6]'}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            </button>
          )}
        </div>

        <div className="w-full max-w-[200px] flex justify-end timesheet-dropdown-wrap">
          <CustomDropdown
            value={selectedTab}
            onChange={(val: any) => setSelectedTab(val)}
            isDark={isDark}
            className="h-10 text-sm font-semibold rounded-md border-[#B59143]/40 w-full"
            options={[
              { value: "new", label: "New Timesheet" },
              { value: "rejected", label: "Rejected Timesheet" },
              { value: "history", label: "History" }
            ]}
          />
        </div>
      </div>

      {/* New Timesheets Tab */}
      {selectedTab === "new" && (
        <div key="new-tab-content">
          <div key="weekly-view-container">
            {/* === WEEKLY VIEW GRID === */}

            {isWeekSubmitted() ? (
              <div className={`flex flex-col items-center justify-center py-16 px-4 mb-6 rounded-xl border border-dashed ${isDark ? 'border-[#B59143]/30 bg-[#14161B]' : 'border-[#B59143]/40 bg-gray-50'}`}>
                <div className="w-16 h-16 bg-[#B59143]/10 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-[#B59143]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className={`text-lg font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Weekly Timesheet Submitted</h3>
                <p className={`text-sm text-center max-w-md ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Your timesheet for {format(selectedWeekStart, "dd MMM")} — {format(addDays(selectedWeekStart, 5), "dd MMM yyyy")} has already been submitted for approval.
                </p>
                <button
                  onClick={() => setSelectedTab("history")}
                  className="mt-6 text-sm font-bold text-[#B59143] hover:underline flex items-center gap-2"
                >
                  View in History
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                </button>
              </div>
            ) : (
              <div key="weekly-grid-active">
                <div className={`excel-table-container ${isDark ? 'bg-[#0A0A0E]' : 'bg-white'} mb-6 rounded-lg border ${isDark ? 'border-[#2E2F33]' : 'border-gray-200'}`}>
                  <table className="excel-table">
                    <thead>
                      <tr>
                        <th className="w-[48px] min-w-[48px] text-center excel-sticky-l1">S.No</th>
                        <th className="w-[200px] min-w-[200px] excel-sticky-l2">List</th>
                        <th className="w-[160px] min-w-[160px]">Cost Center</th>
                        <th className="w-[280px] min-w-[280px]">Description</th>
                        {weekDays.map((d) => (
                          <th key={d.date} className="w-[75px] min-w-[75px] text-center">
                            {d.label}
                          </th>
                        ))}
                        <th className={`w-[85px] min-w-[85px] text-center excel-sticky-divider-left ${weeklySavedMode ? 'excel-sticky-r1' : 'excel-sticky-r2'}`}>Total</th>
                        {!weeklySavedMode && <th className="w-[65px] min-w-[65px] text-center excel-sticky-r1">Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {/* 1. Leave Rows */}
                      {weekLeaves.map((leave, lIdx) => (
                        <tr key={`leave-${lIdx}`} className="excel-leave-row" style={{ backgroundColor: 'var(--excel-leave-bg)' }}>
                          <td className="excel-sticky-l1">
                            <div className="excel-cell-text excel-cell-text-center font-bold">{lIdx + 1}</div>
                          </td>
                          <td className="excel-sticky-l2">
                            <div className="excel-cell-text font-bold" style={{ color: 'var(--excel-leave-text)' }}>{leave.type}</div>
                          </td>
                          <td>
                            <div className="excel-cell-text excel-cell-text-center" style={{ color: 'var(--excel-leave-text)' }}>-</div>
                          </td>
                          <td>
                            <div className="excel-cell-text excel-cell-text-center" style={{ color: 'var(--excel-leave-text)' }}>-</div>
                          </td>
                          {weekDays.map((d) => (
                            <td key={d.date}>
                              <div className="excel-cell-text excel-cell-text-center font-bold" style={{ color: 'var(--excel-leave-text)' }}>
                                {leave.days.includes(d.date) ? leave.type : "0"}
                              </div>
                            </td>
                          ))}
                          <td className={`excel-sticky-divider-left ${weeklySavedMode ? 'excel-sticky-r1' : 'excel-sticky-r2'}`}>
                            <div className="excel-cell-text excel-cell-text-center font-bold" style={{ color: 'var(--excel-leave-text)' }}>0.0</div>
                          </td>
                        </tr>
                      ))}

                      {/* 2. Holiday Rows */}
                      {weekHolidays.map((holiday, hIdx) => (
                        <tr key={`holiday-${hIdx}`} className="excel-leave-row" style={{ backgroundColor: 'var(--excel-leave-bg)' }}>
                          <td className="excel-sticky-l1">
                            <div className="excel-cell-text excel-cell-text-center font-bold">{weekLeaves.length + hIdx + 1}</div>
                          </td>
                          <td className="excel-sticky-l2">
                            <div className="excel-cell-text font-bold truncate" title={holiday.reason} style={{ color: 'var(--excel-leave-text)' }}>{holiday.reason}</div>
                          </td>
                          <td>
                            <div className="excel-cell-text excel-cell-text-center" style={{ color: 'var(--excel-leave-text)' }}>-</div>
                          </td>
                          <td>
                            <div className="excel-cell-text excel-cell-text-center" style={{ color: 'var(--excel-leave-text)' }}>-</div>
                          </td>
                          {weekDays.map((d) => (
                            <td key={d.date}>
                              <div className="excel-cell-text excel-cell-text-center font-bold" style={{ color: 'var(--excel-leave-text)' }} title={holiday.days.includes(d.date) ? holiday.reason : ""}>
                                {holiday.days.includes(d.date) ? "Holiday" : "0"}
                              </div>
                            </td>
                          ))}
                          <td className={`excel-sticky-divider-left ${weeklySavedMode ? 'excel-sticky-r1' : 'excel-sticky-r2'}`}>
                            <div className="excel-cell-text excel-cell-text-center font-bold" style={{ color: 'var(--excel-leave-text)' }}>0.0</div>
                          </td>
                        </tr>
                      ))}

                      {/* 3. Task Rows */}
                      {weeklyRows.map((row, idx) => {
                        const rowTotal = weekDays.reduce((sum, d) => sum + (parseFloat(row.hours[d.date]) || 0), 0);
                        const serial = weekLeaves.length + weekHolidays.length + idx + 1;
                        return (
                          <tr key={idx} style={{ backgroundColor: 'var(--excel-cell-bg)' }}>
                            <td className="excel-sticky-l1">
                              <div className="excel-cell-text excel-cell-text-center font-bold">{serial}</div>
                            </td>
                            <td className="excel-sticky-l2">
                              {weeklySavedMode ? (
                                <div className="excel-cell-text truncate" title={row.project}>{row.project || "Select Project"}</div>
                              ) : (
                                <CustomDropdown
                                  value={row.project}
                                  onChange={async (val) => {
                                    const chs = await fetchChannelsForWeeklyRow(val);
                                    setWeeklyRows((prev) => {
                                      const updated = [...prev];
                                      updated[idx] = { ...updated[idx], project: val, jobCode: "", channels: chs };
                                      return updated;
                                    });
                                  }}
                                  isDark={isDark}
                                  placeholder="Select Project"
                                  className="excel-dropdown"
                                  options={[{ value: "", label: "Select Project" }, ...assignedProjects.map(p => ({ value: p.title, label: p.title }))]}
                                  onFocus={() => setActiveField("project")}
                                  onBlur={() => setActiveField(null)}
                                />
                              )}
                            </td>
                            <td>
                              {weeklySavedMode ? (
                                <div className="excel-cell-text truncate" title={row.jobCode}>{row.jobCode || "Select Stage"}</div>
                              ) : (
                                <CustomDropdown
                                  value={row.jobCode}
                                  onChange={(val) => updateWeeklyRow(idx, "jobCode", val)}
                                  isDark={isDark}
                                  placeholder="Select Stage"
                                  className="excel-dropdown"
                                  options={[{ value: "", label: "Select Stage" }, ...row.channels.map(c => ({ value: c, label: c }))]}
                                  onFocus={() => setActiveField("costCenter")}
                                  onBlur={() => setActiveField(null)}
                                />
                              )}
                            </td>
                            <td>
                              {weeklySavedMode ? (
                                <div
                                  className="excel-cell-text truncate"
                                  title={row.taskDescription}
                                >
                                  {row.taskDescription || "..."}
                                </div>
                              ) : (
                                <AutoResizingTextarea
                                  placeholder="..."
                                  value={row.taskDescription}
                                  rows={1}
                                  onChange={(e: any) => updateWeeklyRow(idx, "taskDescription", e.target.value)}
                                  onFocus={() => setActiveField("description")}
                                  onBlur={() => setActiveField(null)}
                                  className="excel-textarea"
                                />
                              )}
                            </td>
                            {getWeekDays(selectedWeekStart).map((day) => (
                              <td
                                key={day.date}
                                style={{
                                  backgroundColor: (isLeaveDay(day.date) || isHolidayDay(day.date)) ? 'var(--excel-leave-bg)' : undefined
                                }}
                                title={isLeaveDay(day.date) ? "On Leave" : (getHolidayReason(day.date) || "")}
                              >
                                {weeklySavedMode ? (
                                  <div className="excel-cell-text excel-cell-text-center font-bold">
                                    {row.hours[day.date] && row.hours[day.date] !== "0" ? row.hours[day.date] : (getLeaveType(day.date) || (isHolidayDay(day.date) ? "Holiday" : "0"))}
                                  </div>
                                ) : (
                                  <input
                                    type="number"
                                    min="0"
                                    max="24"
                                    step="0.5"
                                    value={row.hours[day.date] ?? ""}
                                    placeholder={getLeaveType(day.date) || (isHolidayDay(day.date) ? "Holiday" : "0")}
                                    onChange={(e) => updateWeeklyRow(idx, "hours", { [day.date]: e.target.value })}
                                    className="excel-input"
                                  />
                                )}
                              </td>
                            ))}
                            {/* Row Total Column */}
                            <td className={`excel-sticky-divider-left ${weeklySavedMode ? 'excel-sticky-r1' : 'excel-sticky-r2'}`}>
                              <div className="excel-cell-text excel-cell-text-center font-bold" style={{ color: isDark ? '#FBD282' : '#000000' }}>
                                {rowTotal.toFixed(1)}
                              </div>
                            </td>
                            {!weeklySavedMode && tsPerms.canDelete({ userId }) && (
                              <td className="excel-sticky-r1">
                                <div className="excel-cell-text excel-cell-text-center">
                                  <button
                                    onClick={() => removeWeeklyRow(idx)}
                                    className={`inline-flex items-center p-1.5 rounded-full text-red-600 ${isDark ? 'hover:bg-[#2A3441]' : 'hover:bg-red-50'} focus:outline-none transition-colors`}
                                    title="Delete Row"
                                  >
                                    <TrashSvg />
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        )
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ backgroundColor: 'var(--excel-header-bg)' }}>
                        <td className="excel-sticky-l1"></td>
                        <td className="excel-sticky-l2"></td>
                        <td></td>
                        <td>
                          <div className="excel-cell-text justify-end font-bold">Total Hours:</div>
                        </td>
                        {weekDays.map((day) => (
                          <td key={day.date}>
                            <div className="excel-cell-text excel-cell-text-center font-bold" style={{ color: isDark ? '#FBD282' : '#000000' }}>
                              {weeklyRows.reduce((sum, row) => sum + (parseFloat(row.hours[day.date]) || 0), 0).toFixed(1)}
                            </div>
                          </td>
                        ))}
                        <td className={`excel-sticky-divider-left ${weeklySavedMode ? 'excel-sticky-r1' : 'excel-sticky-r2'}`}>
                          <div className="excel-cell-text excel-cell-text-center font-black text-sm" style={{ color: isDark ? '#FBD282' : '#000000' }}>
                            {weeklyRows.reduce((total, row) => total + Object.values(row.hours).reduce((s, h) => s + (parseFloat(h) || 0), 0), 0).toFixed(1)}
                          </div>
                        </td>
                        {!weeklySavedMode && <td className="excel-sticky-r1"></td>}
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Actions Footer */}
                <div className="flex flex-col md:flex-row items-start justify-between gap-4 mt-6">
                  <div className="flex items-center gap-6">
                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-black'}`}>Reportee</span>
                    {weeklySavedMode ? (
                      <div className={`min-w-[200px] h-9 px-3 flex items-center border border-gray-300 rounded-md font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {managers.find(m => m.id === weeklyManagerId)?.name || "Select Reportee"}
                      </div>
                    ) : (
                      <CustomDropdown
                        value={weeklyManagerId}
                        onChange={(val) => setWeeklyManagerId(val)}
                        isDark={isDark}
                        className="min-w-[200px] h-9"
                        options={[{ value: "", label: "Select Reportee" }, ...managers.map(m => ({ value: m.id, label: m.name }))]}
                      />
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {weeklySavedMode ? (
                      <div key="weekly-actions-saved" className="flex gap-3">
                        {tsPerms.canEdit({ userId }) && <button onClick={() => setWeeklySavedMode(false)} className={`px-6 py-2 rounded-md text-sm border font-medium ${isDark ? 'border-[#2E2F33] text-[#9CA3AF]' : 'border-gray-200 text-gray-700'} hover:${isDark ? 'bg-[#2A3441]' : 'bg-gray-50'} transition-colors`}>Edit</button>}
                        {tsPerms.canCreate && <button onClick={handleWeeklySubmit} disabled={weeklySaving} className={`px-6 py-2 bg-gradient-to-r from-[#FBD282] to-[#D4AF37] text-white rounded-md text-sm font-bold shadow-md hover:shadow-lg transition-all disabled:opacity-50`}>{weeklySaving ? "Submitting..." : "Submit"}</button>}
                      </div>
                    ) : (
                      <div key="weekly-actions-editing" className="flex gap-3">
                        <button onClick={() => setWeeklySavedMode(true)} className={`px-5 py-2 rounded-md text-sm border font-medium ${isDark ? 'border-[#2E2F33] text-[#9CA3AF]' : 'border-gray-200 text-gray-500'} hover:${isDark ? 'bg-[#2A3441]' : 'bg-gray-50'} transition-colors`}>Cancel</button>
                        {tsPerms.canEdit({ userId }) && <button onClick={handleWeeklySaveClick} className={`px-8 py-2 bg-gradient-to-r from-[#FBD282] to-[#D4AF37] text-white rounded-md text-sm font-bold shadow-md hover:shadow-lg transition-all`}>Save</button>}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {selectedTab === "rejected" && (
        <div className={`mt-2 ${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33]' : 'bg-white border-gray-200'} rounded-lg overflow-hidden`}>
          <div className="excel-table-container w-full relative shadow-sm">
            <table className="excel-table">
              <thead>
                <tr>
                  <th className="w-[50px] min-w-[50px] text-center">S No</th>
                  <th className="w-[90px] min-w-[90px] text-center">Date</th>
                  <th className="w-[180px] min-w-[180px]">Project</th>
                  <th className="w-[150px] min-w-[150px]">Channel</th>
                  <th className="w-[280px] min-w-[280px]">Description</th>
                  <th className="w-[80px] min-w-[80px] text-center">Hours</th>
                  <th className="w-[80px] min-w-[80px] text-center">Total</th>
                  <th className="w-[200px] min-w-[200px]">Comments</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const weekDateStrings = weekDays.map(wd => wd.date);
                  const filtered = selectedWeekStartStr === "ALL"
                    ? rejectedTimesheets
                    : rejectedTimesheets.filter(e => weekDateStrings.includes(e.date));

                  if (filtered.length === 0) {
                    return (
                      <tr>
                        <td colSpan={8} className="px-6 py-12 text-center text-gray-500 text-sm italic">
                          No rejected records found for the selected period.
                        </td>
                      </tr>
                    );
                  }

                  const groupedFiltered: Record<string, any[]> = {};
                  filtered.forEach(e => {
                    const d = e.date;
                    if (!groupedFiltered[d]) groupedFiltered[d] = [];
                    groupedFiltered[d].push(e);
                  });

                  const sortedDatesFiltered = Object.keys(groupedFiltered).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
                  let sNoCounterRejected = 1;

                  return sortedDatesFiltered.map((dateStr) => {
                    const dateEntries = groupedFiltered[dateStr];
                    const dailyTotal = dateEntries.reduce((sum, e) => sum + (parseFloat(e.hours) || 0), 0);
                    const currentSNo = sNoCounterRejected++;

                    return dateEntries.map((entry, idx) => (
                      <tr key={entry.id || idx} style={{ backgroundColor: 'var(--excel-cell-bg)' }}>
                        {idx === 0 && (
                          <>
                            <td rowSpan={dateEntries.length} style={{ verticalAlign: 'middle' }}>
                              <div className="excel-cell-text excel-cell-text-center font-bold">{currentSNo}</div>
                            </td>
                            <td rowSpan={dateEntries.length} style={{ verticalAlign: 'middle' }}>
                              <div className="excel-cell-text excel-cell-text-center">{format(new Date(dateStr), "MMM dd")}</div>
                            </td>
                          </>
                        )}
                        <td>
                          {rejectedSavedMode ? (
                            <div className="excel-cell-text truncate" title={entry.project}>{entry.project}</div>
                          ) : (
                            <CustomDropdown
                              value={entry.project}
                              onChange={async (val) => {
                                const chs = await fetchChannelsForWeeklyRow(val);
                                const currentCode = entry.jobCode || entry.channel;
                                const newJobCode = chs.includes(currentCode) ? currentCode : "";
                                setRejectedTimesheets(prev => prev.map(e => e.id === entry.id ? { ...e, project: val, jobCode: newJobCode, channel: newJobCode, channels: chs } : e));
                              }}
                              isDark={isDark}
                              className="excel-dropdown"
                              options={[{ value: "", label: "Select Project" }, ...assignedProjects.map(p => ({ value: p.title, label: p.title }))]}
                            />
                          )}
                        </td>
                        <td>
                          {rejectedSavedMode ? (
                            <div className="excel-cell-text truncate" title={entry.jobCode || entry.channel}>{entry.jobCode || entry.channel}</div>
                          ) : (
                            <CustomDropdown
                              value={entry.jobCode || entry.channel}
                              onChange={(val) => {
                                setRejectedTimesheets(prev => prev.map(e => e.id === entry.id ? { ...e, jobCode: val, channel: val } : e));
                              }}
                              isDark={isDark}
                              className="excel-dropdown"
                              options={[{ value: "", label: "Select Stage" }, ...(entry.channels || []).map((c: string) => ({ value: c, label: c }))]}
                            />
                          )}
                        </td>
                        <td>
                          {rejectedSavedMode ? (
                            <div className="excel-cell-text truncate" title={entry.taskDescription}>
                              {entry.taskDescription || '-'}
                            </div>
                          ) : (
                            <AutoResizingTextarea
                              value={entry.taskDescription}
                              rows={1}
                              onChange={(e: any) => setRejectedTimesheets(prev => prev.map(ent => ent.id === entry.id ? { ...ent, taskDescription: e.target.value } : ent))}
                              className="excel-textarea"
                            />
                          )}
                        </td>
                        <td>
                          {rejectedSavedMode ? (
                            <div className="excel-cell-text excel-cell-text-center">{entry.hours}</div>
                          ) : (
                            <input
                              type="number"
                              value={entry.hours}
                              onChange={(e) => setRejectedTimesheets(prev => prev.map(ent => ent.id === entry.id ? { ...ent, hours: e.target.value } : ent))}
                              className="excel-input"
                            />
                          )}
                        </td>
                        {idx === 0 && (
                          <td rowSpan={dateEntries.length} style={{ verticalAlign: 'middle' }}>
                            <div className="excel-cell-text excel-cell-text-center font-bold">{dailyTotal.toFixed(1)}</div>
                          </td>
                        )}
                        <td>
                          <div className="excel-cell-text whitespace-pre-wrap leading-tight" style={{ fontWeight: 'normal' }}>
                            {entry.rejectionReason || '-'}
                          </div>
                        </td>
                      </tr>
                    ));
                  });
                })()}
              </tbody>
              <tfoot>
                <tr style={{ backgroundColor: 'var(--excel-header-bg)' }}>
                  <td colSpan={5}>
                    <div className="excel-cell-text justify-end font-bold uppercase tracking-wider text-[10px]">
                      Grand Total:
                    </div>
                  </td>
                  <td></td>
                  <td>
                    <div className="excel-cell-text excel-cell-text-center font-black text-sm" style={{ color: isDark ? '#FFFFFF' : '#000000' }}>
                      {(() => {
                        const weekDateStrings = weekDays.map(wd => wd.date);
                        const filtered = selectedWeekStartStr === "ALL"
                          ? rejectedTimesheets
                          : rejectedTimesheets.filter(e => weekDateStrings.includes(e.date));
                        const total = filtered.reduce((sum, e) => sum + (parseFloat(e.hours?.toString() || "0") || 0), 0);
                        return total.toFixed(1);
                      })()}
                    </div>
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Sticky Bottom Actions Bar */}
          <div className={`sticky bottom-0 z-50 flex flex-col md:flex-row items-center justify-between gap-4 p-4 mt-6 border-t ${isDark ? 'bg-transparent border-[#2E2F33]' : 'bg-transparent border-gray-200'}`}>
            <div className="flex items-center gap-6">
              <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Reportee</span>
              {rejectedSavedMode ? (
                <div className={`min-w-[200px] h-9 px-3 flex items-center border border-gray-300 rounded-md font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {managers.find(m => m.id === rejectedManagerId)?.name || "Select Reportee"}
                </div>
              ) : (
                <CustomDropdown
                  value={rejectedManagerId}
                  onChange={(val) => setRejectedManagerId(val)}
                  isDark={isDark}
                  className="min-w-[200px] h-9 bg-white"
                  options={[{ value: "", label: "Select Reportee" }, ...managers.map(m => ({ value: m.id, label: m.name }))]}
                />
              )}
            </div>
            <div className="flex items-center gap-3">
              {rejectedSavedMode ? (
                <div key="rejected-actions-saved" className="flex gap-3">
                  {tsPerms.canEdit({ userId }) && <button
                    onClick={async () => {
                      // Pre-fetch channels for all current rejected entries
                      const updated = await Promise.all(rejectedTimesheets.map(async (e) => {
                        if (!e.channels) {
                          const chs = await fetchChannelsForWeeklyRow(e.project);
                          return { ...e, channels: chs };
                        }
                        return e;
                      }));
                      setRejectedTimesheets(updated);
                      setRejectedSavedMode(false);
                    }}
                    className={`h-9 px-6 rounded-md text-sm border font-bold transition-all ${isDark ? 'border-[#2E2F33] text-[#FBD282] bg-transparent hover:bg-[#FBD282]/10' : 'border-[#B59143]/20 text-[#B59143] bg-white hover:bg-gray-50'}`}
                  >
                    Edit
                  </button>}
                  {tsPerms.canCreate && <button onClick={handleRejectedSubmit} disabled={rejectedSaving || (selectedWeekStartStr !== "ALL" && !rejectedTimesheets.some(e => weekDays.some(wd => wd.date === e.date)))} className={`h-9 px-8 bg-gradient-to-r from-[#FBD282] to-[#D4AF37] text-white rounded-md text-sm font-bold shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:grayscale`}>{rejectedSaving ? "Submitting..." : "Submit"}</button>}
                </div>
              ) : (
                <div key="rejected-actions-editing" className="flex gap-3">
                  <button onClick={() => setRejectedSavedMode(true)} className={`h-9 px-6 rounded-md text-sm border font-bold transition-all ${isDark ? 'border-[#2E2F33] text-gray-400 bg-transparent hover:bg-gray-800' : 'border-gray-200 text-gray-500 bg-white hover:bg-gray-50'}`}>Cancel</button>
                  <button onClick={() => setRejectedSavedMode(true)} className={`h-9 px-8 bg-gradient-to-r from-[#FBD282] to-[#D4AF37] text-white rounded-md text-sm font-bold shadow-md hover:shadow-lg transition-all`}>Save</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* History Tab */}
      {selectedTab === "history" && (
        <div className={`mt-2 ${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33]' : 'bg-white border-gray-200'} rounded-lg overflow-hidden`}>
          <div className="excel-table-container w-full relative shadow-sm">
            <table className="excel-table">
              <thead>
                <tr>
                  <th className="w-[50px] min-w-[50px] text-center">S No</th>
                  <th className="w-[90px] min-w-[90px] text-center">Date</th>
                  <th className="w-[180px] min-w-[180px]">Project</th>
                  <th className="w-[150px] min-w-[150px]">Channel</th>
                  <th className="w-[280px] min-w-[280px]">Description</th>
                  <th className="w-[80px] min-w-[80px] text-center">Hours</th>
                  <th className="w-[80px] min-w-[80px] text-center">Total</th>
                  <th className="w-[160px] min-w-[160px]">Comments</th>
                  <th className="w-[100px] min-w-[100px] text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // 1. Filter history entries by currently selected week
                  const weekDateStrings = weekDays.map(wd => wd.date);
                  let filtered = selectedWeekStartStr === "ALL"
                    ? historyTimesheets
                    : historyTimesheets.filter(e => weekDateStrings.includes(e.date));

                  const leaveEntries: any[] = [];
                  if (selectedWeekStartStr !== "ALL") {
                    weekLeaves.forEach(leave => {
                      leave.days.forEach(dayStr => {
                        leaveEntries.push({
                          id: `leave-${leave.type}-${dayStr}`,
                          date: dayStr,
                          project: "Leave",
                          channel: "-",
                          taskDescription: leave.type,
                          hours: "0.0",
                          isLeave: true,
                          status: "approved"
                        });
                      });
                    });
                  }

                  // Merge and sort by date descending
                  const allEntries = [...filtered, ...leaveEntries].sort((a, b) => {
                    const dateA = new Date(a.date).getTime();
                    const dateB = new Date(b.date).getTime();
                    if (dateA !== dateB) return dateB - dateA;
                    if (a.isLeave && !b.isLeave) return -1;
                    if (!a.isLeave && b.isLeave) return 1;
                    return 0;
                  });

                  if (allEntries.length === 0) {
                    return (
                      <tr>
                        <td colSpan={9} className="px-6 py-12 text-center text-gray-500 text-sm italic">
                          No records found for {format(selectedWeekStart, "dd MMM")} — {format(addDays(selectedWeekStart, 5), "dd MMM yyyy")}.
                        </td>
                      </tr>
                    );
                  }

                  const grouped: Record<string, any[]> = {};
                  allEntries.forEach(e => {
                    const d = e.date;
                    if (!grouped[d]) grouped[d] = [];
                    grouped[d].push(e);
                  });

                  const sortedDates = Object.keys(grouped).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
                  let sNoCounter = 1;

                  return sortedDates.map((dateStr) => {
                    const dateEntries = grouped[dateStr];
                    const dailyTotal = dateEntries.reduce((sum, e) => sum + (parseFloat(e.hours) || 0), 0);
                    const currentSNo = sNoCounter++;

                    return dateEntries.map((entry, idx) => (
                      <tr key={entry.id || idx} style={{ backgroundColor: entry.isLeave ? 'var(--excel-leave-bg)' : 'var(--excel-cell-bg)' }}>
                        {idx === 0 && (
                          <>
                            <td rowSpan={dateEntries.length} style={{ verticalAlign: 'middle' }}>
                              <div className="excel-cell-text excel-cell-text-center font-bold">{currentSNo}</div>
                            </td>
                            <td rowSpan={dateEntries.length} style={{ verticalAlign: 'middle' }}>
                              <div className="excel-cell-text excel-cell-text-center">{format(new Date(dateStr), "MMM dd")}</div>
                            </td>
                          </>
                        )}
                        <td>
                          <div className="excel-cell-text truncate" title={entry.project} style={{ color: entry.isLeave ? 'var(--excel-leave-text)' : undefined }}>
                            {entry.project}
                          </div>
                        </td>
                        <td>
                          <div className="excel-cell-text truncate" title={entry.jobCode || entry.channel} style={{ color: entry.isLeave ? 'var(--excel-leave-text)' : undefined }}>
                            {entry.jobCode || entry.channel}
                          </div>
                        </td>
                        <td>
                          <div className="excel-cell-text truncate" title={entry.taskDescription || '-'} style={{ color: entry.isLeave ? 'var(--excel-leave-text)' : undefined }}>
                            {entry.taskDescription || '-'}
                          </div>
                        </td>
                        <td>
                          <div className="excel-cell-text excel-cell-text-center" style={{ color: entry.isLeave ? 'var(--excel-leave-text)' : undefined }}>
                            {entry.hours}
                          </div>
                        </td>
                        {idx === 0 && (
                          <td rowSpan={dateEntries.length} style={{ verticalAlign: 'middle' }}>
                            <div className="excel-cell-text excel-cell-text-center font-bold">{dailyTotal.toFixed(1)}</div>
                          </td>
                        )}
                        <td>
                          <div className="excel-cell-text truncate" title={entry.rejectionReason || '-'}>
                            {entry.rejectionReason || '-'}
                          </div>
                        </td>
                        <td>
                          <div className="excel-cell-text excel-cell-text-center">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase font-bold ${entry.status.toLowerCase() === "approved" || entry.status.toLowerCase() === "approve" ? "bg-green-100 text-green-700" :
                              entry.status.toLowerCase() === "rejected" || entry.status.toLowerCase() === "reject" ? "bg-red-100 text-red-700" :
                                "bg-yellow-100 text-yellow-700"
                              }`}>
                              {entry.status || "pending"}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ));
                  });
                })()}
              </tbody>
              <tfoot>
                <tr style={{ backgroundColor: 'var(--excel-header-bg)' }}>
                  <td colSpan={5}>
                    <div className="excel-cell-text justify-end font-bold uppercase tracking-wider text-[10px]">
                      Grand Total:
                    </div>
                  </td>
                  <td></td>
                  <td>
                    <div className="excel-cell-text excel-cell-text-center font-black text-sm" style={{ color: isDark ? '#FFFFFF' : '#000000' }}>
                      {(() => {
                        const weekDateStrings = weekDays.map(wd => wd.date);
                        const filtered = selectedWeekStartStr === "ALL"
                          ? historyTimesheets
                          : historyTimesheets.filter(e => weekDateStrings.includes(e.date));
                        const total = filtered.reduce((sum, e) => sum + (parseFloat(e.hours?.toString() || "0") || 0), 0);
                        return total.toFixed(1);
                      })()}
                    </div>
                  </td>
                  <td></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}


      {/* Task Tooltip Overlay */}
      {tooltip && (
        <div
          className={`fixed z-[9999] px-3 py-2 rounded-lg shadow-xl text-xs leading-relaxed max-w-xs pointer-events-none border ${isDark
            ? 'bg-[#2A2D35] text-gray-100 border-[#3E4049]'
            : 'bg-white text-gray-800 border-gray-200'
            }`}
          style={{
            left: tooltip.x,
            top: tooltip.y - 8,
            transform: 'translateY(-100%)',
          }}
        >
          {tooltip.text}
          {/* Caret */}
          <span
            className={`absolute top-full left-4 border-[5px] border-transparent ${isDark ? 'border-t-[#3E4049]' : 'border-t-gray-200'}`}
            style={{ marginTop: '-1px' }}
          />
          <span
            className={`absolute top-full left-4 border-[5px] border-transparent ${isDark ? 'border-t-[#2A2D35]' : 'border-t-white'}`}
            style={{ marginTop: '-2px' }}
          />
        </div>
      )}

      {/* Add Modal */}
      {isAddOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div
            role="dialog"
            aria-modal="true"
            className={`relative w-full max-w-2xl ${isDark ? 'bg-[#1A1C1F] border-[#2E2F33] text-white' : 'bg-white border-gray-100 text-gray-900'} rounded-2xl shadow-2xl border overflow-hidden transform transition-all duration-200 scale-100`}
          >
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#FBD282] via-[#E8C16C] to-[#B59143]" />
            <div className="p-6 sm:p-8">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className={`text-xl font-semibold ${isDark ? 'text-[#FBD282]' : 'text-[#B59143]'}`}>New Timesheet</h3>
                  <p className="text-sm text-gray-500 mt-1">Log your work details for approval.</p>
                </div>
                <button
                  onClick={() => {
                    setIsAddOpen(false);
                    resetForm();
                  }}
                  className={`inline-flex items-center justify-center w-9 h-9 rounded-full border ${theme.border} ${theme.textMuted} hover:${isDark ? 'bg-[#2A3441]' : 'bg-gray-50'} focus:outline-none`}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className={`text-xs uppercase tracking-wider font-bold ${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>DATE</label>
                  <div className="relative group cursor-pointer">
                    <div
                      className={`w-full flex items-center justify-between rounded-md px-4 py-2 text-xs border shadow-sm transition-all ${isDark
                        ? 'bg-[#0B0B0F] text-white border-[#2E2F33] hover:border-[#E8C16C]/50 focus:ring-2 focus:ring-[#E8C16C]/20'
                        : 'bg-white text-gray-900 border-gray-200 hover:border-[#E8C16C]/50 focus:ring-4 focus:ring-[#E8C16C]/10'
                        }`}
                      onClick={() => (document.getElementById('tsHiddenDate') as HTMLInputElement)?.showPicker()}
                    >
                      <span className={!date ? (isDark ? 'text-gray-500' : 'text-gray-400') : ''}>
                        {date ? formatDateDDMMYYYY(date) : "dd-mm-yyyy"}
                      </span>
                      <svg className="w-4 h-4 text-gray-400 group-hover:text-[#E8C16C] transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <input
                      id="tsHiddenDate"
                      type="date"
                      className="absolute inset-0 opacity-0 pointer-events-none"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label className={`text-xs uppercase tracking-wider font-bold ${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>Project</label>
                  <CustomDropdown
                    value={project}
                    onChange={(value) => {
                      console.log("Project selected:", value);
                      setProject(value);
                    }}
                    isDark={isDark}
                    className="w-full"
                    options={[
                      { value: "", label: "Select Project" },
                      ...assignedProjects.map((p) => ({
                        value: p.title,
                        label: p.title
                      }))
                    ]}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label className={`text-xs uppercase tracking-wider font-bold ${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>Channel</label>
                  <CustomDropdown
                    value={jobCode}
                    onChange={(value) => setJobCode(value)}
                    isDark={isDark}
                    className="w-full"
                    options={[
                      { value: "", label: "Select Channel" },
                      ...channels.map((c, idx) => ({
                        value: c,
                        label: c
                      }))
                    ]}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>Hours</label>
                  <input
                    type="number"
                    placeholder="Hours worked"
                    value={hours}
                    onChange={(e) => setHours(e.target.value)}
                    className={`w-full rounded-md px-4 py-1.5 text-sm placeholder-gray-500 transition-all border shadow-sm outline-none ${isDark
                      ? 'bg-[#0B0B0F] text-white border-[#2E2F33] focus:ring-2 focus:ring-[#E8C16C]/20 focus:border-[#E8C16C]/50'
                      : 'bg-white text-gray-900 border-gray-200 focus:ring-4 focus:ring-[#E8C16C]/10 focus:border-[#E8C16C]/50'
                      }`}
                  />
                </div>

                <div className="md:col-span-2 flex flex-col gap-2">
                  <label className={`text-xs uppercase tracking-wider font-bold ${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>Task Description</label>
                  <textarea
                    placeholder="Describe what you worked on..."
                    value={taskDescription}
                    rows={3}
                    onChange={(e) => setTaskDescription(e.target.value)}
                    className={`w-full rounded-md px-4 py-1.5 text-sm placeholder-gray-500 transition-all border shadow-sm outline-none ${isDark
                      ? 'bg-[#0B0B0F] text-white border-[#2E2F33] focus:ring-2 focus:ring-[#E8C16C]/20 focus:border-[#E8C16C]/50'
                      : 'bg-white text-gray-900 border-gray-200 focus:ring-4 focus:ring-[#E8C16C]/10 focus:border-[#E8C16C]/50'
                      }`}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label className={`text-xs uppercase tracking-wider font-bold ${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>Manager</label>
                  <CustomDropdown
                    value={managerId}
                    onChange={(value) => setManagerId(value)}
                    isDark={isDark}
                    className="w-full"
                    options={[
                      { value: "", label: "Select Manager" },
                      ...managers.map((m) => ({
                        value: m.id,
                        label: m.name
                      }))
                    ]}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-8">
                <button
                  onClick={() => {
                    setIsAddOpen(false);
                    resetForm();
                  }}
                  className={`px-4 py-2.5 rounded-lg border ${theme.border} ${theme.textMuted} hover:${isDark ? 'bg-[#2A3441]' : 'bg-gray-50'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAdd}
                  disabled={saving}
                  className={`px-8 py-2.5 rounded-xl font-bold text-white shadow-lg transition-all transform active:scale-[0.98] ${saving
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-[#E8C16C] to-[#B59143] hover:shadow-[0_8px_20px_-6px_rgba(181,145,67,0.5)]'
                    }`}
                >
                  {saving ? "Saving..." : "Create Entry"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div
            role="dialog"
            aria-modal="true"
            className={`relative w-full max-w-2xl ${isDark ? 'bg-[#1A1C1F] border-[#2E2F33] text-white' : 'bg-white border-gray-100 text-gray-900'} rounded-2xl shadow-2xl border overflow-hidden transform transition-all duration-200`}
          >
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#FBD282] via-[#E8C16C] to-[#B59143]" />
            <div className="p-6 sm:p-8">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className={`text-xl font-semibold ${isDark ? 'text-[#FBD282]' : 'text-[#B59143]'}`}>Edit Rejected Timesheet</h3>
                  <p className="text-sm text-gray-500 mt-1">Update the entry and resubmit for approval.</p>
                </div>
                <button
                  onClick={() => setIsEditOpen(false)}
                  className={`inline-flex items-center justify-center w-9 h-9 rounded-full border ${theme.border} ${theme.textMuted} hover:${isDark ? 'bg-[#2A3441]' : 'bg-gray-50'} focus:outline-none`}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <label className={`text-xs font-medium ${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>DATE</label>
                  <div className="relative group cursor-pointer">
                    <div
                      className={`w-full flex items-center justify-between rounded-md px-4 py-2 text-xs border shadow-sm transition-all ${isDark
                        ? 'bg-[#0B0B0F] text-white border-[#2E2F33] hover:border-[#E8C16C]/50 focus:ring-2 focus:ring-[#E8C16C]/20'
                        : 'bg-white text-gray-900 border-gray-200 hover:border-[#E8C16C]/50 focus:ring-4 focus:ring-[#E8C16C]/10'
                        }`}
                      onClick={() => (document.getElementById('editTsHiddenDate') as HTMLInputElement)?.showPicker()}
                    >
                      <span className={!editDate ? (isDark ? 'text-gray-500' : 'text-gray-400') : ''}>
                        {editDate ? formatDateDDMMYYYY(editDate) : "dd-mm-yyyy"}
                      </span>
                      <svg className="w-4 h-4 text-gray-400 group-hover:text-[#E8C16C] transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <input
                      id="editTsHiddenDate"
                      type="date"
                      className="absolute inset-0 opacity-0 pointer-events-none"
                      value={editDate}
                      onChange={(e) => setEditDate(e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <label className={`text-xs font-medium ${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>Project</label>
                  <CustomDropdown
                    value={editProject}
                    onChange={(value) => {
                      setEditProject(value);
                      setEditJobCode("");
                    }}
                    isDark={isDark}
                    className="w-full"
                    options={[
                      { value: "", label: "Select Project" },
                      ...assignedProjects.map((p) => ({
                        value: p.title,
                        label: p.title
                      }))
                    ]}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className={`text-xs font-medium ${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>Channel</label>
                  <CustomDropdown
                    value={editJobCode}
                    onChange={(value) => setEditJobCode(value)}
                    isDark={isDark}
                    className="w-full"
                    options={[
                      { value: "", label: "Select Channel" },
                      ...channelsForEdit.map((c, idx) => ({
                        value: c,
                        label: c
                      }))
                    ]}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className={`text-xs font-medium ${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>Hours</label>
                  <input
                    type="number"
                    placeholder="Hours worked"
                    value={editHours}
                    onChange={(e) => setEditHours(e.target.value)}
                    className={`w-full rounded-md px-4 py-1.5 text-sm placeholder-gray-500 transition-all border shadow-sm outline-none ${isDark
                      ? 'bg-[#0B0B0F] text-white border-[#2E2F33] focus:ring-2 focus:ring-[#E8C16C]/20 focus:border-[#E8C16C]/50'
                      : 'bg-white text-gray-900 border-gray-200 focus:ring-4 focus:ring-[#E8C16C]/10 focus:border-[#E8C16C]/50'
                      }`}
                  />
                </div>

                <div className="md:col-span-2 flex flex-col gap-1">
                  <label className={`text-xs font-medium ${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>Task Description</label>
                  <textarea
                    placeholder="Describe what you worked on..."
                    value={editTask}
                    rows={3}
                    onChange={(e) => setEditTask(e.target.value)}
                    className={`w-full rounded-md px-4 py-1.5 text-sm placeholder-gray-500 transition-all border shadow-sm outline-none ${isDark
                      ? 'bg-[#0B0B0F] text-white border-[#2E2F33] focus:ring-2 focus:ring-[#E8C16C]/20 focus:border-[#E8C16C]/50'
                      : 'bg-white text-gray-900 border-gray-200 focus:ring-4 focus:ring-[#E8C16C]/10 focus:border-[#E8C16C]/50'
                      }`}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className={`text-xs font-medium ${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>Manager</label>
                  <CustomDropdown
                    value={editManager}
                    onChange={(value) => setEditManager(value)}
                    isDark={isDark}
                    className="w-full"
                    options={[
                      { value: "", label: "Select Manager" },
                      ...managers.map((m) => ({
                        value: m.id,
                        label: m.name
                      }))
                    ]}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-8">
                <button
                  onClick={() => setIsEditOpen(false)}
                  className={`px-4 py-2.5 rounded-lg border ${theme.border} ${theme.textMuted} hover:${isDark ? 'bg-[#2A3441]' : 'bg-gray-50'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={editSaving}
                  className={`px-8 py-2.5 rounded-xl font-bold text-white shadow-lg transition-all transform active:scale-[0.98] ${editSaving
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-[#E8C16C] to-[#B59143] hover:shadow-[0_8px_20px_-6px_rgba(181,145,67,0.5)]'
                    }`}
                >
                  {editSaving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeSheetEntry;
